import { apiFetch } from "../api.js";

export type ConnectionProvider = "plaid" | "snaptrade" | "manual";
export type ConnectionStatus =
  | "active"
  | "needs_reauth"
  | "disconnected"
  | "error";

export interface Connection {
  connection_id: string;
  household_id: string;
  provider: ConnectionProvider;
  provider_item_id?: string;
  institution_name: string;
  institution_id?: string;
  status: ConnectionStatus;
  last_sync_at?: string;
  last_successful_sync_at?: string;
  next_sync_at?: string;
  sync_frequency?: "daily" | "manual";
  scopes?: string[];
  error_count?: number;
  last_error?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface StagingTransaction {
  connection_id: string;
  external_tx_id: string;
  proposed_transaction: {
    transaction_type: string;
    plaid_transaction_id?: string;
    plaid_account_id?: string;
    date?: string;
    merchant_name?: string;
    amount_cents?: number;
    direction?: "outflow" | "inflow";
    iso_currency_code?: string;
    plaid_personal_finance_category?: unknown;
    pending?: boolean;
    category_id?: string;
  };
  status: "pending_review" | "approved" | "rejected" | "duplicate";
  reviewed_by?: string;
  reviewed_at?: string;
  promoted_transaction_id?: string;
  notes?: string;
  created_at: string;
}

export interface ConnectionsListResponse {
  connections: Connection[];
  count: number;
}

export interface StagingListResponse {
  status: string;
  count: number;
  staging_transactions: StagingTransaction[];
}

export interface LinkTokenResponse {
  link_token: string;
  expiration: string;
}

export interface ExchangeResponse {
  connection_id: string;
  institution_name: string;
  institution_id?: string;
  provider: ConnectionProvider;
  status: ConnectionStatus;
}

export interface SyncResponse {
  connection_id: string;
  added_count: number;
  modified_skipped?: number;
  removed_skipped?: number;
  skipped_unknown_type?: number;
  status: "success" | "partial" | "failure";
  error?: string;
  skipped?: boolean;
  reason?: string;
}

export interface SnaptradeLoginRedirectResponse {
  redirect_uri: string;
}

export interface SnaptradeExchangeResponse {
  connections: Array<{
    connection_id: string;
    institution_name: string;
    authorization_id: string;
  }>;
  count: number;
}

export interface BatchActionResponse {
  action: "approved" | "rejected";
  updated: number;
  skipped: number;
  error_count: number;
  errors: Array<{ id: string; reason: string }>;
}

export function listConnections(): Promise<ConnectionsListResponse> {
  return apiFetch<ConnectionsListResponse>("/v1/integrations");
}

export function listStagingTransactions(
  status: "pending_review" | "approved" | "rejected" = "pending_review",
): Promise<StagingListResponse> {
  return apiFetch<StagingListResponse>(
    `/v1/integrations/staging?status=${encodeURIComponent(status)}`,
  );
}

export function createPlaidLinkToken(): Promise<LinkTokenResponse> {
  return apiFetch<LinkTokenResponse>("/v1/integrations/plaid/link-token", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function exchangePlaidToken(input: {
  public_token: string;
  institution_name: string;
  institution_id?: string;
}): Promise<ExchangeResponse> {
  return apiFetch<ExchangeResponse>("/v1/integrations/plaid/exchange", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function syncConnection(connectionId: string): Promise<SyncResponse> {
  return apiFetch<SyncResponse>(
    `/v1/integrations/${connectionId}/sync`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function disconnectConnection(
  connectionId: string,
): Promise<{ connection_id: string; deleted: boolean }> {
  return apiFetch(`/v1/integrations/${connectionId}`, { method: "DELETE" });
}

export function batchApproveStaging(
  ids: Array<{ connection_id: string; external_tx_id: string }>,
): Promise<BatchActionResponse> {
  return apiFetch("/v1/integrations/staging/batch-approve", {
    method: "POST",
    body: JSON.stringify({ staging_ids: ids }),
  });
}

export function batchRejectStaging(
  ids: Array<{ connection_id: string; external_tx_id: string }>,
): Promise<BatchActionResponse> {
  return apiFetch("/v1/integrations/staging/batch-reject", {
    method: "POST",
    body: JSON.stringify({ staging_ids: ids }),
  });
}

export function snaptradeLoginRedirect(): Promise<SnaptradeLoginRedirectResponse> {
  return apiFetch<SnaptradeLoginRedirectResponse>(
    "/v1/integrations/snaptrade/login-redirect",
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function snaptradeExchange(): Promise<SnaptradeExchangeResponse> {
  return apiFetch<SnaptradeExchangeResponse>(
    "/v1/integrations/snaptrade/exchange",
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function patchStagingTransaction(args: {
  connection_id: string;
  external_tx_id: string;
  category_id?: string;
  merchant_override?: string;
  notes?: string;
}): Promise<StagingTransaction> {
  const { connection_id, external_tx_id, ...body } = args;
  return apiFetch<StagingTransaction>(
    `/v1/integrations/staging/${encodeURIComponent(
      connection_id,
    )}/${encodeURIComponent(external_tx_id)}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}
